import Axios from "axios";

import $http from "@/helpers/http";
import Logger from "@/helpers/logger";
import { generateCrid, getClioAPIHeaders } from "@/helpers/api";
import { getUserAgentHeader } from "@/helpers/userAgent";

class EmailApi {
  getEmailList() {
    try {
      return $http.get("panel/account/email/list");
    } catch (e) {
      Logger.error("`getEmailList` api", e);
      return Promise.reject(e);
    }
  }

  createEmail(payload) {
    try {
      return $http.post("panel/account/email/create", payload);
    } catch (e) {
      Logger.error("`createEmail` api", e);
      return Promise.reject(e);
    }
  }

  getCatchAll() {
    try {
      return $http.get("panel/domain/catchall/get");
    } catch (e) {
      Logger.error("`getCatchAll` api", e);
      return Promise.reject(e);
    }
  }

  getEmailForwarders() {
    try {
      return $http.get("panel/account/forwarder/list");
    } catch (e) {
      Logger.error("`getEmailForwarders` api", e);
      return Promise.reject(e);
    }
  }

  fetchImportTasks() {
    try {
      return $http.get("panel/mail-import/fetch-import-tasks");
    } catch (e) {
      Logger.error("`fetchImportTasks` api", e);
      return Promise.reject(e);
    }
  }

  getAllSuspensions(accountId) {
    try {
      return $http.get(`panel/account/email/get-all-suspensions?accountId=${accountId}`);
    } catch (e) {
      Logger.error("`getAllSuspensions` api", e);
      return Promise.reject(e);
    }
  }

  importEmail(payload) {
    try {
      return $http.post("/panel/mail-import/schedule", payload);
    } catch (e) {
      Logger.error("`importEmail` api", e);
      return Promise.reject(e);
    }
  }

  resumeImportEmail(payload, token, bundleId) {
    try {
      return Axios.post("/panel/mail-import/schedule", payload, {
        headers: {
          "X-Auth": "controlPanel",
          "X-User-Agent": getUserAgentHeader(),
          "Content-Type": "application/json",
          Authorization: token,
        },
        params: {
          bundleId,
        },
      });
    } catch (e) {
      Logger.error("`resumeImportEmail` api", e);
      return Promise.reject(e);
    }
  }

  deleteAndCreate(payload) {
    try {
      return $http.post("panel/account/email/delete-and-create", payload);
    } catch (e) {
      Logger.error("`deleteAndCreate` api", e);
      return Promise.reject(e);
    }
  }

  removeAlias(payload) {
    try {
      return $http.post("panel/account/email/alias/remove", payload);
    } catch (e) {
      Logger.error("`removeAlias` api", e);
      return Promise.reject(e);
    }
  }

  updateAlias(payload) {
    try {
      return $http.post("panel/account/email/alias/update", payload);
    } catch (e) {
      Logger.error("`updateAlias` api", e);
      return Promise.reject(e);
    }
  }

  setCatchAll(payload) {
    try {
      return $http.post("panel/domain/catchall/set", payload);
    } catch (e) {
      Logger.error("`setCatchAll` api", e);
      return Promise.reject(e);
    }
  }

  removeCatchAll(payload) {
    try {
      return $http.post("panel/domain/catchall/remove", payload);
    } catch (e) {
      Logger.error("`removeCatchAll` api", e);
      return Promise.reject(e);
    }
  }

  deleteEmail(payload) {
    try {
      return $http.post("panel/account/email/delete", payload);
    } catch (e) {
      Logger.error("`deleteEmail` api", e);
      return Promise.reject(e);
    }
  }

  updateConfig(payload) {
    try {
      return $http.post("panel/account/email/update/config", payload);
    } catch (e) {
      Logger.error("`updateConfig` api", e);
      return Promise.reject(e);
    }
  }

  getAliasList(params) {
    try {
      return $http.get("panel/account/email/alias/list", { params });
    } catch (e) {
      Logger.error("`getAliasList` api", e);
      return Promise.reject(e);
    }
  }

  addAlias(payload) {
    try {
      return $http.post("panel/account/email/alias/add", payload);
    } catch (e) {
      Logger.error("`addAlias` api", e);
      return Promise.reject(e);
    }
  }

  resetPassword(payload) {
    try {
      return $http.post("panel/account/email/reset-password", payload);
    } catch (e) {
      Logger.error("`resetPassword` api", e);
      return Promise.reject(e);
    }
  }

  suspend(payload) {
    try {
      return $http.post("panel/account/email/suspend", payload);
    } catch (e) {
      Logger.error("`suspend` api", e);
      return Promise.reject(e);
    }
  }

  unsuspend(payload) {
    try {
      return $http.post("panel/account/email/unsuspend", payload);
    } catch (e) {
      Logger.error("`unsuspend` api", e);
      return Promise.reject(e);
    }
  }

  createForwarder(payload) {
    try {
      return $http.post("panel/account/forwarder/create", payload);
    } catch (e) {
      Logger.error("`createForwarder` api", e);
      return Promise.reject(e);
    }
  }

  deleteForwarder(payload) {
    try {
      return $http.post("panel/account/forwarder/delete", payload);
    } catch (e) {
      Logger.error("`deleteForwarder` api", e);
      return Promise.reject(e);
    }
  }

  forwarderRecipientAdd(payload) {
    try {
      return $http.post("panel/account/forwarder/recipients/add", payload);
    } catch (e) {
      Logger.error("`forwarderRecipientAdd` api", e);
      return Promise.reject(e);
    }
  }

  forwarderRecipientRemove(payload) {
    try {
      return $http.post("panel/account/forwarder/recipients/remove", payload);
    } catch (e) {
      Logger.error("`forwarderRecipientRemove` api", e);
      return Promise.reject(e);
    }
  }

  sendLoginDetails(payload) {
    try {
      return $http.post("panel/account/email/send-login-details", payload);
    } catch (e) {
      Logger.error("`sendLoginDetails` api", e);
      return Promise.reject(e);
    }
  }

  getEmailAccountBackupState({ aids, bid }) {
    try {
      return $http.post(
        "users/state",
        {
          bid,
          rt: 0,
          crid: generateCrid("GUSR"),
          aids,
        },
        getClioAPIHeaders()
      );
    } catch (e) {
      Logger.error("`getEmailAccountBackupState` api", e);
      return Promise.reject(e);
    }
  }
}

const emailApi = new EmailApi();

export default emailApi;
